import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/backend/api-client";

export function useLPAccount(userId: string) {
  return useQuery({
    queryKey: ["lp-account", userId],
    queryFn: async () => {
      if (!userId) return null;
      try {
        const response = await apiClient.get(`/api/lp/my-account?user_id=${userId}`);
        return response as any;
      } catch (error: any) {
        // If account doesn't exist (404), return null instead of throwing
        // This is expected for non-LP users
        if (error?.status === 404 || error?.message?.includes("not found")) {
          return null;
        }
        throw error;
      }
    },
    enabled: !!userId,
    retry: false, // Don't retry 404s - they're expected for non-LP users
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    staleTime: 60000, // Cache for 1 minute
    // Note: 404s may still show in console, use useLPStatus for checking access
  });
}

export function useLPPoolStats(userId: string) {
  return useQuery({
    queryKey: ["lp-pool-stats", userId],
    queryFn: async () => {
      if (!userId) return null;
      const response = await apiClient.get(`/api/lp/pool-stats?user_id=${userId}`);
      return response as any;
    },
    enabled: !!userId,
  });
}

export function useLPEarnings(userId: string, period: string = "30d") {
  return useQuery({
    queryKey: ["lp-earnings", userId, period],
    queryFn: async () => {
      if (!userId) return null;
      const response = await apiClient.get(`/api/lp/earnings?user_id=${userId}&period=${period}`);
      return response as any;
    },
    enabled: !!userId,
  });
}

export function useLPEarningsChart(period: string = "30d") {
  return useQuery({
    queryKey: ["lp-earnings-chart", period],
    queryFn: async () => {
      const response = await apiClient.get(`/api/lp/earnings-chart?period=${period}`);
      const data = response as any;
      // Transform to frontend format
      return (data.data_points || []).map((point: any) => ({
        date: point.date,
        tradingFees: parseFloat(point.trading_fees),
        liquidations: parseFloat(point.liquidations),
        funding: parseFloat(point.funding),
        traderPnlGains: parseFloat(point.trader_pnl_gains),
        traderPnlLosses: parseFloat(point.trader_pnl_losses),
        total: parseFloat(point.total),
      }));
    },
  });
}

